import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';

const DistrictSchemeVisualization = ({ 
  selectedState, 
  stateData, 
  districtData, 
  selectedScheme,
  selectedFinancialYear,
  selectedDistrict,
  onDistrictClick,
  districtWiseData = [],
  districtWiseSchemeData = {},
  loadingDistrictData = false
}) => {
  const [hoverInfo, setHoverInfo] = useState(null);
  const [highlightedDistrict, setHighlightedDistrict] = useState(null);
  const [filteredDistrictData, setFilteredDistrictData] = useState(null);
  const [districtCodeMapping, setDistrictCodeMapping] = useState({});
  const geoJsonRef = useRef();
  const map = useMap();
  const hoverRef = useRef();

  // FIXED: Tooltip is now a DOM element instead of Leaflet tooltip
  const tooltipRef = useRef(null);

  // Create and manage the tooltip DOM element
  useEffect(() => {
    // Create tooltip element
    if (!tooltipRef.current) {
      tooltipRef.current = document.createElement('div');
      tooltipRef.current.className = 'district-center-tooltip';
      tooltipRef.current.style.display = 'none';
      document.body.appendChild(tooltipRef.current);
    }

    // Update tooltip position and content
    if (hoverInfo && !selectedDistrict) {
      tooltipRef.current.innerHTML = `
        <div class="district-tooltip-content">
          <h6>${hoverInfo.name}</h6>
          <hr>
         <p><strong>Surveys:</strong> ${hoverInfo.surveys}</p>
         
        </div>
      `;
      tooltipRef.current.style.display = 'block';
      
      // Position in center of viewport
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      tooltipRef.current.style.left = '50%';
      tooltipRef.current.style.top = '50%';
      tooltipRef.current.style.transform = 'translate(-50%, -50%)';
    } else {
      tooltipRef.current.style.display = 'none';
    }

    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.style.display = 'none';
      }
    };
  }, [hoverInfo, selectedDistrict, selectedFinancialYear]);

  // Clean up tooltip on unmount
  useEffect(() => {
    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
    };
  }, []);

  // FIXED: Improved district filtering with better error handling
  useEffect(() => {
    if (!selectedState || !districtData) {
      console.log("❌ Missing required data:", {
        selectedState: !!selectedState,
        districtData: !!districtData
      });
      setFilteredDistrictData(null);
      setDistrictCodeMapping({});
      return;
    }

    try {
      const stateName = selectedState.properties?.STATE;
      if (!stateName) {
        console.error("❌ Selected state has no STATE property:", selectedState);
        setFilteredDistrictData(null);
        return;
      }

      console.log("🔍 Filtering districts for state:", stateName);
      
      const features = districtData.features || districtData;
      
      if (!features || !Array.isArray(features)) {
        console.error("❌ Invalid district data format:", districtData);
        setFilteredDistrictData(null);
        return;
      }

      const filteredDistricts = {
        type: "FeatureCollection",
        features: features.filter(feature => {
          if (!feature || !feature.properties) return false;
          return feature.properties.STATE_UT === stateName;
        })
      };

      console.log("✅ Filtered districts:", {
        stateName,
        totalDistricts: features.length,
        filteredDistricts: filteredDistricts.features.length,
        sample: filteredDistricts.features.slice(0, 3).map(f => f.properties.DISTRICT)
      });

      setFilteredDistrictData(filteredDistricts);
    } catch (error) {
      console.error("❌ Error filtering districts:", error);
      setFilteredDistrictData(null);
    }
  }, [selectedState, districtData]);

  // FIXED: Improved district code mapping with better error handling
  useEffect(() => {
    if (!filteredDistrictData || !districtWiseData.length) {
      setDistrictCodeMapping({});
      return;
    }

    try {
      const mapping = {};
      let matchCount = 0;

      console.log("🔄 Creating district code mapping...");

      districtWiseData.forEach(apiDistrict => {
        if (!apiDistrict) return;
        
        const apiDistrictCode = apiDistrict.districtCode ? apiDistrict.districtCode.toString() : null;
        const apiDistrictName = apiDistrict.districtName || 'Unknown';
        
        if (!apiDistrictCode) {
          console.log("❌ No districtCode in API data for:", apiDistrictName);
          return;
        }

        const matchingGeoDistrict = filteredDistrictData.features.find(geoFeature => {
          if (!geoFeature || !geoFeature.properties) return false;
          const geoDistrictCode = geoFeature.properties.Dist_LGD ? geoFeature.properties.Dist_LGD.toString() : null;
          return geoDistrictCode === apiDistrictCode;
        });

        if (matchingGeoDistrict) {
          const districtName = matchingGeoDistrict.properties.DISTRICT;
          mapping[apiDistrictCode] = {
            surveyCount: apiDistrict.totalSurvey || 0,
            districtName: districtName,
            apiDistrictName: apiDistrictName
          };
          matchCount++;
          
          console.log(`✅ Code Match: API "${apiDistrictName}" (${apiDistrictCode}) -> GeoJSON "${districtName}" = ${apiDistrict.totalSurvey} surveys`);
        } else {
          console.log(`❌ No GeoJSON match for API district: ${apiDistrictName} (code: ${apiDistrictCode})`);
          
          const nameMatch = filteredDistrictData.features.find(geoFeature => {
            if (!geoFeature || !geoFeature.properties) return false;
            return geoFeature.properties.DISTRICT.toLowerCase() === apiDistrictName.toLowerCase();
          });
          
          if (nameMatch) {
            const districtName = nameMatch.properties.DISTRICT;
            const geoCode = nameMatch.properties.Dist_LGD;
            mapping[geoCode] = {
              surveyCount: apiDistrict.totalSurvey || 0,
              districtName: districtName,
              apiDistrictName: apiDistrictName,
              matchedByName: true
            };
            matchCount++;
            console.log(`🔄 Name Fallback Match: API "${apiDistrictName}" -> GeoJSON "${districtName}" (code: ${geoCode})`);
          }
        }
      });

      setDistrictCodeMapping(mapping);
    } catch (error) {
      console.error("❌ Error creating district code mapping:", error);
      setDistrictCodeMapping({});
    }
  }, [filteredDistrictData, districtWiseData]);

  // Function to get color based on survey count
  const getColor = (count) => {
    const value = parseInt(count) || 0;
    if (value === 0) return '#f8f9fa';
    if (value < 10) return '#ffeda0';
    if (value < 50) return '#fed976';
    if (value < 100) return '#feb24c';
    if (value < 500) return '#fd8d3c';
    if (value < 1000) return '#fc4e2a';
    if (value < 5000) return '#e31a1c';
    return 'rgba(189, 9, 0, 1)';
  };

  // FIXED: Get survey count by district code (Dist_LGD)
  const getDistrictSurveyCount = (districtFeature) => {
    if (!districtFeature || !districtFeature.properties) return 0;
    
    const districtCode = districtFeature.properties.Dist_LGD ? districtFeature.properties.Dist_LGD.toString() : null;
    
    if (!districtCode || Object.keys(districtCodeMapping).length === 0) {
      return 0;
    }
    
    const match = districtCodeMapping[districtCode];
    return match ? match.surveyCount : 0;
  };

  // Handle district click
  const handleDistrictClick = useCallback((districtFeature) => {
    if (!districtFeature || !districtFeature.properties) return;
    
    const districtName = districtFeature.properties.DISTRICT;
    const districtCode = districtFeature.properties.Dist_LGD;
    const surveyCount = getDistrictSurveyCount(districtFeature);
    
    console.log("🗺️ District clicked:", {
      name: districtName,
      code: districtCode,
      surveys: surveyCount
    });
    
    setHighlightedDistrict(districtName);
    
    if (onDistrictClick) {
      onDistrictClick(districtFeature);
    }
    
    try {
      const bounds = L.geoJSON(districtFeature).getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { 
          padding: [20, 20],
          maxZoom: 12 
        });
      }
    } catch (error) {
      console.error("❌ Error zooming to district:", error);
    }
  }, [onDistrictClick, map, districtCodeMapping]);

  // UPDATED Style function for district boundaries
  const districtStyle = (feature) => {
    if (!feature || !feature.properties) {
      return {
        fillColor: 'transparent',
        weight: 0,
        opacity: 0,
        color: 'transparent',
        fillOpacity: 0,
        zIndex: 1
      };
    }

    const districtName = feature.properties.DISTRICT;
    const districtCode = feature.properties.Dist_LGD;
    const surveyCount = getDistrictSurveyCount(feature);
    const isHighlighted = highlightedDistrict === districtName;
    const isSelected = selectedDistrict?.properties?.DISTRICT === districtName;
    
    if (selectedDistrict) {
      return {
        fillColor: 'transparent',
        weight: 0,
        opacity: 0,
        color: 'transparent',
        fillOpacity: 0,
        zIndex: 1
      };
    }
    
    if (isHighlighted || isSelected) {
      return {
        fillColor: '#a0e018ff',
        weight: 4,
        opacity: 1,
        color: 'white',
        fillOpacity: 0.8,
        zIndex: 1000
      };
    }
    
    return {
      fillColor: getColor(surveyCount),
      weight: surveyCount > 0 ? 2 : 1,
      opacity: 1,
      color: surveyCount > 0 ? 'white' : '#ccc',
      dashArray: surveyCount > 0 ? '3' : '',
      fillOpacity: surveyCount > 0 ? 0.7 : 0.3,
      zIndex: 100
    };
  };

  // UPDATED Function to handle hover events - simplified
  const onEachDistrict = (feature, layer) => {
    if (!feature || !feature.properties || !layer) return;

    const districtName = feature.properties.DISTRICT;
    const districtCode = feature.properties.Dist_LGD;
    const surveyCount = getDistrictSurveyCount(feature);
    const schemeName = selectedScheme ? selectedScheme.schemeName : 'All Schemes';
    const isSelected = selectedDistrict?.properties?.DISTRICT === districtName;

    const match = districtCodeMapping[districtCode];
    const apiDistrictName = match ? match.apiDistrictName : districtName;

    if (selectedDistrict) {
      return;
    }

    layer.on('mouseover', function (e) {
      try {
        // Set hover info for central tooltip
        setHoverInfo({
          latlng: e.latlng,
          name: districtName,
          apiName: apiDistrictName,
          surveys: surveyCount.toLocaleString(),
          scheme: schemeName,
          code: districtCode
        });
        
        if (!isSelected && highlightedDistrict !== districtName) {
          layer.setStyle({
            weight: 3,
            color: '#333',
            dashArray: '',
            fillOpacity: 0.9,
            zIndex: 500
          });
        }
        
        if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
          layer.bringToFront();
        }
      } catch (error) {
        console.error("❌ Error in mouseover event:", error);
      }
    });
    
    layer.on('mouseout', function (e) {
      try {
        // Clear hover info to hide the tooltip
        setHoverInfo(null);
        if (!isSelected && highlightedDistrict !== feature.properties.DISTRICT) {
          if (geoJsonRef.current) {
            geoJsonRef.current.resetStyle(e.target);
          }
        }
      } catch (error) {
        console.error("❌ Error in mouseout event:", error);
      }
    });

    layer.on('click', function (e) {
      try {
        handleDistrictClick(feature);
      } catch (error) {
        console.error("❌ Error in click event:", error);
      }
    });
  };

  // Clear highlight when state changes
  useEffect(() => {
    setHighlightedDistrict(null);
  }, [selectedState]);

  // Enhanced Debug logging
  useEffect(() => {
    console.log("🔍 DistrictSchemeVisualization State:", {
      selectedState: selectedState?.properties?.STATE,
      selectedDistrict: selectedDistrict?.properties?.DISTRICT,
      selectedScheme: selectedScheme?.schemeName,
      selectedFinancialYear,
      districtWiseDataCount: districtWiseData.length,
      filteredDistrictDataCount: filteredDistrictData?.features?.length || 0,
      districtCodeMappingCount: Object.keys(districtCodeMapping).length,
      loadingDistrictData
    });

    if (Object.keys(districtCodeMapping).length > 0) {
      console.log("🎯 Sample District Code Matches:", 
        Object.entries(districtCodeMapping).slice(0, 5).map(([code, data]) => 
          `${code}: ${data.districtName} -> ${data.surveyCount} surveys`
        )
      );
    }
  }, [selectedState, selectedDistrict, selectedScheme, selectedFinancialYear, districtWiseData, filteredDistrictData, districtCodeMapping, loadingDistrictData]);

  if (selectedDistrict) {
    console.log("🚫 District selected - hiding district visualization");
    return null;
  }

  if (!selectedState) {
    console.log("🚫 No state selected - hiding district visualization");
    return null;
  }

  if (!loadingDistrictData && (!filteredDistrictData || !filteredDistrictData.features || filteredDistrictData.features.length === 0)) {
    console.log("🚫 No filtered district data available and not loading");
    return (
      <div className="leaflet-top leaflet-right">
        <div className="leaflet-control leaflet-bar p-2 bg-warning">
          <span>No district data available for {selectedState.properties.STATE}</span>
        </div>
      </div>
    );
  }

  if (loadingDistrictData) {
    return (
      <div className="leaflet-top leaflet-right">
        <div className="leaflet-control leaflet-bar p-2 bg-white">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading district data...</span>
          </div>
          <span className="ms-2">Loading district data...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {filteredDistrictData && (
        <GeoJSON
          ref={geoJsonRef}
          key={`district-schemes-${selectedState.properties.STATE_LGD}-${selectedFinancialYear}-${selectedScheme?.schemeCode || 'all'}-${Object.keys(districtCodeMapping).length}`}
          data={filteredDistrictData}
          style={districtStyle}
          onEachFeature={onEachDistrict}
        />
      )}
      
      {/* Legend */}
      {!selectedDistrict && (
        <div className="leaflet-bottom leaflet-right">
          <div className="leaflet-control leaflet-bar p-2 bg-white" style={{ maxWidth: '70px' }}>
            <div className="text-center mb-1" style={{ fontSize: '9px', fontWeight: 'bold' }}>Surveys</div>
            
            <div className="d-flex">
              <div style={{ position: 'relative', width: '18px', height: '70px', marginRight: '6px' }}>
                <div 
                  style={{ 
                    width: '100%', 
                    height: '100%',
                    background: 'linear-gradient(to top, #ffeda0, #feb24c, #fd8d3c, #e31a1c, #bd0026)',
                    borderRadius: '2px',
                    border: '1px solid #bbb'
                  }}
                ></div>
              </div>
              
              <div className="d-flex flex-column justify-content-between" style={{ height: '70px', fontSize: '8px', color: '#444' }}>
                <div>5K+</div>
                <div>500</div>
                <div>100</div>
                <div>10</div>
                <div>1</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          .district-center-tooltip {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 10000;
  min-width: 150px;
  max-width: 200px;
  background: transparent;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
  border: 2px solid #3498db;
  animation: tooltipFadeIn 0.3s ease-out;
  font-family: Arial, sans-serif;
}
          
          .district-tooltip-content h6 {
            margin: 0 0 12px 0;
            font-weight: bold;
            color: #2c3e50;
            font-size: 18px;
            text-align: center;
          }
          
          .district-tooltip-content hr {
            margin: 10px 0;
            border: none;
            border-top: 2px solid #3498db;
            opacity: 0.6;
          }
          
          .district-tooltip-content p {
            margin: 8px 0;
            font-size: 14px;
            color: #34495e;
            line-height: 1.4;
          }
          
          .district-tooltip-content p strong {
            color: #2c3e50;
            font-weight: 600;
            display: inline-block;
            min-width: 120px;
          }
          
          @keyframes tooltipFadeIn {
            from {
              opacity: 0;
              transform: translate(-50%, -50%) translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translate(-50%, -50%) translateY(0);
            }
          }
          
          /* Ensure it appears above all map layers */
          .leaflet-pane {
            z-index: 400 !important;
          }
        `}
      </style>
    </>
  );
};

export default DistrictSchemeVisualization;